import { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import axios from 'axios';
import { API_URL } from "../../config";
import { jwtDecode } from 'jwt-decode';
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Select from 'react-select';

dayjs.extend(utc);
dayjs.extend(timezone);

const CalendarWithTasks = ({ onDateSelect, selectedDate, onEventClick }) => {
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [tasks, setTasks] = useState([]);
    const [filter, setFilter] = useState('selected');
    const [events, setEvents] = useState([]);
    const [Data4, setData4] = useState([]);
    const [Data6, setData6] = useState([]);
    const [instcity, setinstCity] = useState([]);
    const [userinstData, setUserinstData] = useState([]);
    const [user, setUser] = useState(null);
    const [defaultOption, setDefaultOption] = useState("");
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [auxiliaryDataLoaded, setAuxiliaryDataLoaded] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userOptions, setUserOptions] = useState([]);

    const token = useMemo(() => {
        return localStorage.getItem('jwtToken');
    }, []);

    const axiosConfig = useMemo(() => {
        if (!token) return null;
        return {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            timeout: 15000,
        };
    }, [token]);

    useEffect(() => {
        if (!token) return;
        try {
            const decoded = jwtDecode(token);
            if (decoded) {
                setUser(decoded);
                if (decoded.userId) {
                    setDefaultOption(decoded.userId.toString());
                }
            }
        } catch (error) {
            console.error('Error decoding the token:', error);
            setUser(null);
            setDefaultOption("");
        }
    }, [token]);

    useEffect(() => {
        if (!user?.userId || auxiliaryDataLoaded || !axiosConfig) return;

        const fetchAuxiliaryData = async () => {
            try {
                const [activityResponse, addressResponse, physiciansResponse] = await Promise.all([
                    axios.get(`${API_URL}/api/activitytype`, axiosConfig),
                    axios.get(`${API_URL}/api/adressinstitutions`, axiosConfig),
                    axios.get(`${API_URL}/api/physiciansinstitute`, axiosConfig),
                ]);

                setData4(Array.isArray(activityResponse.data) ? activityResponse.data : []);
                setinstCity(Array.isArray(addressResponse.data) ? addressResponse.data : []);
                setUserinstData(Array.isArray(physiciansResponse.data) ? physiciansResponse.data : []);
            } catch (error) {
                console.error("Error fetching auxiliary data:", error);
                setData4([]);
                setinstCity([]);
                setUserinstData([]);
            } finally {
                setAuxiliaryDataLoaded(true);
            }
        };

        fetchAuxiliaryData();
    }, [user?.userId, auxiliaryDataLoaded, axiosConfig]);

    const fetchCalendarData = useCallback(
        async (month, year) => {
            if (!user?.userId || !auxiliaryDataLoaded || !axiosConfig) return;

            setLoading(true);
            try {
                const userIds = (defaultOption || user.userId || "").toString();
                if (!userIds) {
                    setData6([]);
                    return;
                }

                const response = await axios.post(
                    `${API_URL}/api/4calendaradaug`,
                    {
                        user_ids: userIds,
                        month: month + 1,
                        year: year,
                    },
                    axiosConfig
                );

                const data = Array.isArray(response.data) ? response.data : [];
                setData6(data);
            } catch (error) {
                console.error("Error fetching calendar data:", error);
                setData6([]);
            } finally {
                setLoading(false);
                setInitialLoad(false);
            }
        },
        [user, auxiliaryDataLoaded, axiosConfig, defaultOption]
    );

    useEffect(() => {
        if (!user?.userId || !auxiliaryDataLoaded) return;

        const month = currentMonth.getMonth();
        const year = currentMonth.getFullYear();

        const timer = setTimeout(() => {
            fetchCalendarData(month, year);
        }, 300);

        return () => clearTimeout(timer);
    }, [currentMonth, user?.userId, fetchCalendarData, auxiliaryDataLoaded]);

    const combineDateTime = useCallback((dateString, timeString) => {
        if (!dateString || !timeString) {
            return null;
        }

        try {
            const datePart = dayjs.utc(dateString);
            const timePart = dayjs.utc(timeString);

            if (!datePart.isValid() || !timePart.isValid()) {
                return null;
            }

            const combined = dayjs(
                `${datePart.format("YYYY-MM-DD")}T${timePart.format("HH:mm:ss")}`
            );

            return combined.isValid() ? combined : null;
        } catch (error) {
            console.error('Error combining date time:', error);
            return null;
        }
    }, []);

    useEffect(() => {
        if (!Array.isArray(Data6) || Data6.length === 0 || !auxiliaryDataLoaded) {
            setEvents([]);
            return;
        }

        const processed = Data6.map((detail) => {
            if (!detail || !detail.date || !detail.type) return null;

            try {
                const dateForPlanificat = dayjs(detail.date);
                const visitDate = detail.visit_date || detail.date;

                switch (detail.type) {
                    case 'Raportat': {
                        let title = "Raportare necunoscută";
                        const startRaport = combineDateTime(visitDate, detail.start_time);
                        const endRaport = combineDateTime(visitDate, detail.end_time);

                        if (!startRaport || !endRaport) return null;

                        if (detail.obiect_type_id === 1) {
                            const userInst = userinstData.find(
                                (i) => i.institute_assign_id === detail.object_id
                            );
                            if (userInst) {
                                title = `${userInst.user_name_surname} - ${userInst.spec} - ${userInst.inst}`;
                            } else {
                                title = "Utilizator necunoscut";
                            }
                        } else {
                            const city = instcity.find((i) => i.id === detail.object_id);
                            if (city) {
                                title = `${city.inst} - ${city.adres} - ${city.city_name}`;
                            } else {
                                title = "Oraș necunoscut";
                            }
                        }

                        return {
                            planning_day_id: detail.planning_day_id,
                            title,
                            start: startRaport.toDate(),
                            end: endRaport.toDate(),
                            activity: detail.type,
                            planning_detail_id: detail.planning_detail_id,
                            comment: detail.comment,
                            type: 'Raportat',
                            planning_id: detail.planning_id,
                            is_validated: detail.is_validated,
                            report_detail_id: detail.report_detail_id,
                            user_id: detail.user_id,
                            user_name_surname: detail.user_name_surname,
                            physician_is_present: detail.physician_is_present

                        };
                    }

                    case 'Alte activitati': {
                        const activityType = Data4.find(
                            (i) => i.activity_type_id === detail.activity_type_id
                        );
                        const baseDesc = activityType?.description || '';
                        const baseComment = activityType?.oad_comment || '';
                        const title =
                            `${baseDesc}${baseDesc && baseComment ? ' - ' : ''}${baseComment}` ||
                            'Alte activități';

                        const startRaport = combineDateTime(visitDate, detail.start_time);
                        const endRaport = combineDateTime(visitDate, detail.end_time);
                        if (!startRaport || !endRaport) return null;

                        return {
                            planning_day_id: detail.planning_day_id,
                            title,
                            start: startRaport.toDate(),
                            end: endRaport.toDate(),
                            activity: detail.type,
                            planning_detail_id: detail.planning_detail_id,
                            persons: detail.persons,
                            comment: detail.oad_comment,
                            type: detail.type,
                            planning_id: detail.planning_id,
                            is_validated: detail.is_validated,
                            report_detail_id: detail.report_detail_id,
                            user_id: detail.user_id,
                            user_name_surname: detail.user_name_surname
                        };
                    }

                    case 'Alte activitati Planificate': {
                        const activityType = Data4.find(
                            (i) => i.activity_type_id === detail.activity_type_id
                        );
                        const baseDesc = activityType?.description || '';
                        const baseComment = activityType?.oad_comment || '';
                        const title =
                            `${baseDesc}${baseDesc && baseComment ? ' - ' : ''}${baseComment}` ||
                            'Alte activități';

                        const startPlanned = combineDateTime(detail.date, detail.oad_start_time);
                        const endPlanned = combineDateTime(detail.date, detail.oad_end_time);
                        if (!startPlanned || !endPlanned) return null;

                        return {
                            planning_day_id: detail.planning_day_id,
                            title,
                            start: startPlanned.toDate(),
                            end: endPlanned.toDate(),
                            activity: detail.type,
                            planning_detail_id: detail.planning_detail_id,
                            persons: detail.persons,
                            comment: detail.oad_comment,
                            type: detail.type,
                            planning_id: detail.planning_id,
                            is_validated: detail.is_validated,
                            user_id: detail.user_id,
                            user_name_surname: detail.user_name_surname
                        };
                    }

                    case 'Planificat': {
                        let title = "Necunoscut";

                        if (detail.obiect_type_id === 1) {
                            const userInst = userinstData.find(
                                (i) => i.institute_assign_id === detail.object_id
                            );
                            if (userInst) {
                                title = `${userInst.user_name_surname} - ${userInst.spec} - ${userInst.inst}`;
                            } else {
                                title = "Utilizator necunoscut";
                            }
                        } else {
                            const city = instcity.find((i) => i.id === detail.object_id);
                            if (city) {
                                title = `${city.inst} - ${city.adres} - ${city.city_name}`;
                            } else {
                                title = "Oraș necunoscut";
                            }
                        }

                        let start = dateForPlanificat.hour(8).minute(30).second(0);
                        let end = start.add(30, 'minute');

                        if (!start.isValid() || !end.isValid()) {
                            return null;
                        }

                        return {
                            planning_day_id: detail.planning_day_id,
                            title,
                            start: start.toDate(),
                            end: end.toDate(),
                            activity: detail.type,
                            planning_detail_id: detail.planning_detail_id,
                            type: 'Planificat',
                            planning_id: detail.planning_id,
                            is_validated: detail.is_validated,
                            user_id: detail.user_id,
                            user_name_surname: detail.user_name_surname
                        };
                    }

                    default:
                        return null;
                }
            } catch (err) {
                console.error("Error processing event:", err, detail);
                return null;
            }
        })
            .filter(Boolean)
            .sort((a, b) => {
                const aTime = a?.start ? new Date(a.start).getTime() : 0;
                const bTime = b?.start ? new Date(b.start).getTime() : 0;
                return aTime - bTime;
            });

        setEvents(processed);
    }, [Data6, userinstData, instcity, Data4, auxiliaryDataLoaded, combineDateTime]);


    // useEffect(() => {
    //     if (events.length > 0) {
    //         console.log(events)
    //         const uniqueUsers = [...new Set(events
    //             .map(event => event.user_id)
    //             .filter(Boolean)
    //         )];

    //         const options = uniqueUsers.map(userId => {
    //             const userEvent = events.find(event => event.user_id === userId);
    //             return {
    //                 value: userId.toString(),
    //                 label: userEvent?.user_name_surname || `User ${userId}`
    //             };
    //         });

    //         setUserOptions(options);
    //         console.log(options)
    //     } else {
    //         setUserOptions([]);
    //     }
    // }, [events]);


    useEffect(() => {
        if (!selectedDate || events.length === 0) {
            setUserOptions([]);
            return;
        }

        const selectedStr = selectedDate.toDateString();

        const eventsOnSelectedDate = events.filter(ev =>
            ev.start && new Date(ev.start).toDateString() === selectedStr
        );

        const uniqueUsers = [...new Set(
            eventsOnSelectedDate
                .map(event => event.user_id)
                .filter(Boolean)
        )];

        const options = uniqueUsers.map(userId => {
            const userEvent = eventsOnSelectedDate.find(event => event.user_id === userId);
            return {
                value: userId.toString(),
                label: userEvent?.user_name_surname || `User ${userId}`
            };
        });

        setUserOptions(options);
    }, [events, selectedDate]);

    useEffect(() => {
        if (initialLoad || !Array.isArray(events)) {
            setTasks([]);
            return;
        }

        let filteredEvents = [...events];

        if (selectedUserIds.length > 0) {
            filteredEvents = filteredEvents.filter(event =>
                selectedUserIds.includes(event.user_id?.toString())
            );
        }

        if (filter === 'selected') {
            if (!selectedDate) {
                setTasks([]);
                return;
            }
            const selectedStr = selectedDate.toDateString();
            filteredEvents = filteredEvents.filter(
                (event) => event.start && new Date(event.start).toDateString() === selectedStr
            );
        } else if (filter === 'today') {
            const todayStr = new Date().toDateString();
            filteredEvents = filteredEvents.filter(
                (event) => event.start && new Date(event.start).toDateString() === todayStr
            );
        } else if (filter === 'upcoming') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            filteredEvents = filteredEvents.filter(
                (event) => event.start && new Date(event.start) >= today
            );
        }

        setTasks(filteredEvents);
    }, [events, selectedDate, filter, initialLoad, selectedUserIds]);

    const getFilterTitle = () => {
        switch (filter) {
            case 'selected':
                if (!selectedDate) return 'Evenimente pentru data selectată';
                return `Evenimente pentru ${selectedDate.toLocaleDateString('ro-RO', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                })}`;
            case 'today':
                return 'Evenimente pentru astăzi';
            case 'upcoming':
                return 'Evenimente viitoare';
            case 'all':
                return 'Toate evenimentele';
            default:
                return 'Evenimente';
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);

        const startDate = new Date(firstDay);
        const dayOfWeek = firstDay.getDay() || 7;
        startDate.setDate(firstDay.getDate() - (dayOfWeek - 1));

        const days = [];
        for (let i = 0; i < 42; i++) {
            const day = new Date(startDate);
            day.setDate(startDate.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const navigateMonth = (direction) => {
        setCurrentMonth((prev) => {
            const newMonth = new Date(prev);
            newMonth.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
            return newMonth;
        });
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isSelected = (date) => {
        if (!selectedDate) return false;
        return date.toDateString() === selectedDate.toDateString();
    };

    const isCurrentMonth = (date) => {
        return (
            date.getMonth() === currentMonth.getMonth() &&
            date.getFullYear() === currentMonth.getFullYear()
        );
    };

    const getEventsForDate = (date) => {
        const dateStr = date.toDateString();
        return events.filter(
            (event) =>
                event.start && new Date(event.start).toDateString() === dateStr
        );
    };

    const getEventColor = (event) => {
        const activity = event?.activity || event?.type || '';
        if (activity.startsWith("Planificat")) return "#801F26";
        if (activity.startsWith("Raportat")) {
            if (event?.physician_is_present === false || event?.physician_is_present === 0) {
                return "#42b15fff";
            }

            return "#285133";
        } if (activity.startsWith("Alte activitati Planificate")) return "#332851";
        if (activity.startsWith("Alte activitati")) return "#DA9673";
        return "#666666";
    };

    const handleDateSelect = (date) => {
        if (typeof onDateSelect === 'function') {
            onDateSelect(date);
        }
        setFilter('selected');
    };

    const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth]);
    const weekDays = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

    if (initialLoad && !auxiliaryDataLoaded) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Se încarcă calendarul...</p>
                    <p className="text-sm text-gray-500 mt-2">
                        Aceasta poate dura câteva momente
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row gap-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            <div className="lg:w-2/5">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => navigateMonth('prev')}
                            disabled={loading}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="flex items-center">
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                                {currentMonth.toLocaleDateString('ro-RO', {
                                    month: 'long',
                                    year: 'numeric',
                                })}
                            </h2>
                            {loading && (
                                <div className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            )}
                        </div>

                        <button
                            onClick={() => navigateMonth('next')}
                            disabled={loading}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>


                    <div className="grid grid-cols-7 gap-1 p-2 border-b border-gray-200 dark:border-gray-700">
                        {weekDays.map((day, index) => (
                            <div
                                key={index}
                                className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2"
                            >
                                {day}
                            </div>
                        ))}
                    </div>


                    <div className="grid grid-cols-7 gap-1 p-2">
                        {days.map((day, index) => {
                            const dayEvents = getEventsForDate(day);
                            const isTodayDate = isToday(day);
                            const isSelectedDate = isSelected(day);
                            const isCurrentMonthDate = isCurrentMonth(day);

                            return (
                                <button
                                    key={index}
                                    onClick={() => handleDateSelect(day)}
                                    disabled={loading}
                                    className={`
                    relative h-12 rounded-lg border-2 transition-all
                    ${!isCurrentMonthDate
                                            ? 'text-gray-400 bg-gray-50 dark:bg-gray-900 border-transparent'
                                            : isSelectedDate
                                                ? 'border-blue-500 bg-blue-500 text-white'
                                                : isTodayDate
                                                    ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                                                    : 'border-transparent bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                                >
                                    <span
                                        className={`
                      text-sm font-medium
                      ${isSelectedDate ? 'text-white' : ''}
                    `}
                                    >
                                        {day.getDate()}
                                    </span>

                                    {dayEvents.length > 0 && isCurrentMonthDate && (
                                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1">
                                            {dayEvents.slice(0, 3).map((event, eventIndex) => (
                                                <div
                                                    key={eventIndex}
                                                    className="w-1.5 h-1.5 rounded-full"
                                                    style={{
                                                        backgroundColor: isSelectedDate
                                                            ? 'white'
                                                            : getEventColor(event),
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-medium text-gray-800 dark:text-white mb-2">
                            Legendă
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-[#801F26]"></div>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                    Planificate
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-[#285133]"></div>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                    Raportate
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-[#332851]"></div>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                    Alte Planif.
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-[#DA9673]"></div>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                    Alte Rap.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="lg:w-3/5">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 h-full">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                                {getFilterTitle()}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {tasks.length} evenimente
                            </p>
                        </div>

                        <div className="flex items-center space-x-2">
                            {userOptions.length > 0 && (
                                <div className="relative">
                                    <label className="absolute -top-3 left-2 text-sm bg-white px-1 dark:bg-gray-800 dark:text-gray-100 z-10">
                                        Filtrează după user
                                    </label>

                                    <Select
                                        options={userOptions}
                                        placeholder="Selectează useri..."
                                        isMulti
                                        classNamePrefix="react-select"
                                        value={userOptions.filter(option =>
                                            selectedUserIds.includes(option.value)
                                        )}
                                        onChange={(selectedOptions) => {
                                            const selectedValues = selectedOptions
                                                ? selectedOptions.map(option => option.value)
                                                : [];
                                            setSelectedUserIds(selectedValues);
                                        }}
                                        isDisabled={loading}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            control: (provided) => ({
                                                ...provided,
                                                borderColor: '#424242',
                                                width: '200px',
                                                boxShadow: 'none',
                                                color: '#1F2937',
                                                backgroundColor: '#FFFFFF',
                                                zIndex: 0,
                                                minHeight: '48px',
                                                '&:hover': {
                                                    borderColor: '#424242'
                                                }
                                            }),
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                        {loading ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                <p>Se încarcă evenimentele...</p>
                            </div>
                        ) : tasks.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>Nu există evenimente</p>
                                <p className="text-sm mt-1">Selectează o altă dată sau filtru</p>
                            </div>
                        ) : (
                            tasks.map((event, index) => (
                                <div
                                    key={index}
                                    onClick={() =>
                                        typeof onEventClick === 'function' && onEventClick(event)
                                    }
                                    className="flex items-start p-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors cursor-pointer group relative"
                                >
                                    <div
                                        className="absolute left-0 top-0 h-full w-3 rounded-l-lg"
                                        style={{ backgroundColor: getEventColor(event) }}
                                    />

                                    <div className="ml-5 flex-1">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-sm font-medium text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                                    {event.title || 'Eveniment'}
                                                </h3>
                                                 {!event.physician_is_present && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        <span className="font-medium">Doctorul nu a fost prezent</span>
                                                    </p>
                                                )}
                                                {event.user_name_surname && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        Creat de: <span className="font-medium">{event.user_name_surname}</span>
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 rounded mb-1 mt-1">
                                                    {event.start
                                                        ? new Date(event.start).toLocaleTimeString('ro-RO', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })
                                                        : 'N/A'}
                                                </span>
                                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                                    {event.start
                                                        ? new Date(event.start).toLocaleDateString('ro-RO')
                                                        : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            {event.activity || event.type || 'Eveniment'}
                                        </p>
                                        {event.comment && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                                                {event.comment}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarWithTasks;
